import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
} from 'react'
import {
  arrow,
  autoPlacement,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react'
import Box from '@mui/material/Box'
import Portal from '@mui/material/Portal'
import { alpha, keyframes, useTheme } from '@mui/material/styles'
import {
  displayableStepIndices,
  isStepDisplayable,
  nextDisplayableStepIndex,
} from '@/lib/productTours/tourSelection'
import type {
  ProductTour,
  ProductTourStatus,
  ProductTourStep,
} from '@/lib/productTours/types'
import { useElementRect } from './hooks/use-element-rect'
import { ProductTourCard, TOUR_OUTLINE } from './ProductTourCard'
import { getTourAction, isTourActionAvailable } from './tourActions'
import { tourActionTrigger } from '@/lib/productTours/anchors'
import { findAnchorElement, isAnchorDisplayable } from './utils/dom'

const SPOTLIGHT_PADDING_PX = 6
const BUBBLE_GAP_PX = 12
const VIEWPORT_PADDING_PX = 16

const pulse = keyframes`
  0% { opacity: 0.9; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.06); }
`

type ProductTourOverlayProps = {
  tour: ProductTour
  /** `null` = arrêt sans état (aucune étape affichable). */
  onClose: (status: ProductTourStatus | null) => void
  onStepViewed?: (stepIndex: number) => void
}

const isInside = (
  rect: { top: number; left: number; width: number; height: number },
  x: number,
  y: number
) =>
  x >= rect.left - SPOTLIGHT_PADDING_PX &&
  x <= rect.left + rect.width + SPOTLIGHT_PADDING_PX &&
  y >= rect.top - SPOTLIGHT_PADDING_PX &&
  y <= rect.top + rect.height + SPOTLIGHT_PADDING_PX

/* Au-dessus des fenêtres que le tour ouvre (récap DAU : 1600), sous la barre de chargement (2000). */
export const PRODUCT_TOUR_Z_INDEX = 1700
/* Temps laissé à une action (onglet, panneau) pour faire apparaître l'élément de l'étape. */
export const ACTION_WAIT_MS = 2_000
/* Temps passé sur le bouton déclencheur avant d'ouvrir : le glissement (300 ms) puis un temps de lecture. */
export const TRIGGER_POINT_MS = 900
const OVERSIZED_WIDTH_RATIO = 0.8
const OVERSIZED_HEIGHT_RATIO = 0.6
export const isOversized = (rect: { width: number; height: number }) =>
  rect.width >= window.innerWidth * OVERSIZED_WIDTH_RATIO &&
  rect.height >= window.innerHeight * OVERSIZED_HEIGHT_RATIO

const pointDuration = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 400
    : TRIGGER_POINT_MS
const ACTION_POLL_MS = 50

export const ProductTourOverlay: FC<ProductTourOverlayProps> = ({
  tour,
  onClose,
  onStepViewed,
}) => {
  const theme = useTheme()
  const arrowRef = useRef<SVGSVGElement>(null)
  /* Étapes dont l'élément n'est pas apparu même après leur action : retirées du parcours. */
  const skippedRef = useRef(new Set<number>())
  const displayable = useCallback(
    (candidate: ProductTourStep) =>
      !skippedRef.current.has(tour.steps.indexOf(candidate)) &&
      isStepDisplayable(candidate, isAnchorDisplayable, isTourActionAvailable),
    [tour.steps]
  )
  const [index, setIndex] = useState<number | undefined>(() =>
    nextDisplayableStepIndex(tour.steps, 0, 1, (candidate) =>
      isStepDisplayable(candidate, isAnchorDisplayable, isTourActionAvailable)
    )
  )
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  /* Bouton ou onglet déclencheur montré avant d'ouvrir l'action de l'étape. */
  const [pointerEl, setPointerEl] = useState<HTMLElement | null>(null)
  /* L'étape attend que son action fasse apparaître l'élément : voile seul, pas de bulle. */
  const [resolving, setResolving] = useState(false)
  /* Étapes affichables, relues à chaque étape : la progression ne compte pas les étapes sautées. */
  const [visibleSteps, setVisibleSteps] = useState<number[]>([])
  const primaryButtonRef = useRef<HTMLButtonElement>(null)
  const directionRef = useRef<1 | -1>(1)
  const openedRef = useRef<{ id: string; close?: () => void } | null>(null)
  /* Ancre de la dernière étape affichée : si c'était déjà le déclencheur, inutile de le remontrer. */
  const lastShownAnchorRef = useRef<string | null>(null)
  const step = index === undefined ? undefined : tour.steps[index]

  useEffect(() => {
    if (index === undefined) onClose(null)
  }, [index, onClose])

  /* Fin du tour : on referme ce qu'il a ouvert, l'écran revient à son état d'avant. */
  useEffect(
    () => () => {
      openedRef.current?.close?.()
      openedRef.current = null
    },
    []
  )

  useLayoutEffect(() => {
    if (index === undefined || !step) return

    const opened = openedRef.current
    if (opened && opened.id !== step.action) {
      opened.close?.()
      openedRef.current = null
    }
    const handler =
      step.action && openedRef.current?.id !== step.action
        ? getTourAction(step.action)
        : undefined
    const runAction = () => {
      if (!handler || !step.action) return
      handler.open()
      openedRef.current = { id: step.action, close: handler.close }
    }

    const timers: number[] = []
    const clearTimers = () => {
      timers.forEach((timer) => {
        window.clearTimeout(timer)
        window.clearInterval(timer)
      })
    }

    const show = (element: HTMLElement | null) => {
      element?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
      setPointerEl(null)
      lastShownAnchorRef.current = step.anchor
      setAnchorEl(element)
      setVisibleSteps(displayableStepIndices(tour.steps, displayable))
      setResolving(false)
    }

    const resolve = () => {
      const anchor = step.anchor
      if (anchor === null) {
        show(null)
        return
      }
      const found = findAnchorElement(anchor)
      if (found) {
        show(found)
        return
      }
      setAnchorEl(null)
      setResolving(true)
      const startedAt = Date.now()
      const poll = window.setInterval(() => {
        const element = findAnchorElement(anchor)
        if (element) {
          window.clearInterval(poll)
          show(element)
          return
        }
        if (Date.now() - startedAt < ACTION_WAIT_MS) return
        window.clearInterval(poll)
        /* Absent même après l'action (revue sans anomalie…) : étape retirée, on continue. */
        setPointerEl(null)
        skippedRef.current.add(index)
        const direction = directionRef.current
        const next =
          nextDisplayableStepIndex(
            tour.steps,
            index + direction,
            direction,
            displayable
          ) ?? nextDisplayableStepIndex(tour.steps, index + 1, 1, displayable)
        if (next !== undefined) setIndex(next)
        else onClose('COMPLETED')
      }, ACTION_POLL_MS)
      timers.push(poll)
    }

    /*
     * Avant d'ouvrir, on montre le bouton ou l'onglet qui fait la même chose : l'utilisateur voit
     * d'où vient la fenêtre et saura la rouvrir seul.
     */
    const trigger =
      handler && step.action ? tourActionTrigger(step.action) : undefined
    /* Déjà présenté par l'étape précédente, avec sa bulle : on ouvre directement. */
    const triggerEl =
      trigger && trigger !== lastShownAnchorRef.current
        ? findAnchorElement(trigger)
        : null
    if (triggerEl) {
      triggerEl.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
      setAnchorEl(null)
      setPointerEl(triggerEl)
      setResolving(true)
      timers.push(
        window.setTimeout(() => {
          runAction()
          resolve()
        }, pointDuration())
      )
    } else {
      runAction()
      resolve()
    }
    return clearTimers
    // L'étape seule déclenche la résolution : relancer sur un autre changement rouvrirait l'action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index])

  const position = index === undefined ? -1 : visibleSteps.indexOf(index)
  const hasPrevious = position > 0
  const hasNext = position >= 0 && position < visibleSteps.length - 1

  useEffect(() => {
    if (index === undefined || resolving) return
    onStepViewed?.(index)
    primaryButtonRef.current?.focus()
  }, [index, onStepViewed, resolving])

  const goTo = useCallback(
    (direction: 1 | -1) => {
      if (index === undefined || resolving) return
      directionRef.current = direction
      const next = nextDisplayableStepIndex(
        tour.steps,
        index + direction,
        direction,
        displayable
      )
      if (next !== undefined) {
        setIndex(next)
      } else if (direction === 1) {
        onClose('COMPLETED')
      }
    },
    [displayable, index, onClose, resolving, tour.steps]
  )

  const goToPosition = useCallback(
    (target: number) => {
      const next = visibleSteps.at(target)
      if (index === undefined || next === undefined || resolving) return
      directionRef.current = next < index ? -1 : 1
      setIndex(next)
    },
    [index, resolving, visibleSteps]
  )

  /* Capture : l'écran (raccourcis de /declaration…) ne reçoit pas ces touches pendant le tour. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose('DISMISSED')
      else if (event.key === 'ArrowRight' || event.key === 'Enter') goTo(1)
      else if (event.key === 'ArrowLeft') goTo(-1)
      else return
      event.preventDefault()
      event.stopPropagation()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [goTo, onClose])

  const isAuto = step?.placement === 'auto'
  const { refs, floatingStyles, isPositioned, context } = useFloating({
    open: Boolean(anchorEl),
    elements: { reference: anchorEl },
    placement: step && step.placement !== 'auto' ? step.placement : undefined,
    middleware: [
      offset(BUBBLE_GAP_PX + SPOTLIGHT_PADDING_PX),
      isAuto ? autoPlacement() : flip(),
      shift({ padding: VIEWPORT_PADDING_PX }),
      arrow({ element: arrowRef, padding: 12 }),
    ],
    whileElementsMounted: autoUpdate,
  })
  const rect = useElementRect(anchorEl ?? pointerEl)

  if (index === undefined || !step) return null

  const scrim = alpha(
    theme.palette.common.black,
    theme.palette.mode === 'dark' ? 0.7 : 0.5
  )
  const zIndex = PRODUCT_TOUR_Z_INDEX
  const titleId = `product-tour-${tour.id}-step-title`
  const centered = step.anchor === null
  const focused = (anchorEl !== null || pointerEl !== null) && rect !== null
  /*
   * Élément qui couvre presque tout l'écran (récap DAU…) : aucune place autour pour la bulle, que
   * floating-ui pousserait hors de l'écran. Elle se pose alors au centre, par-dessus l'élément.
   */
  const oversized = anchorEl !== null && rect !== null && isOversized(rect)
  const pad = focused ? SPOTLIGHT_PADDING_PX : 0
  const hole =
    focused && rect
      ? rect
      : {
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
          width: 0,
          height: 0,
        }

  return (
    <Portal>
      {/* Bloque l'écran sous le tour ; le voile vient de l'ombre de la zone mise en avant. */}
      <Box
        data-testid="product-tour-blocker"
        data-product-tour
        /* Clic sur le voile = fermer (TEC-1631) ; un clic sur l'élément mis en avant ne fait rien. */
        onClick={(event) => {
          if (!focused || !isInside(hole, event.clientX, event.clientY)) {
            onClose('DISMISSED')
          }
        }}
        sx={{ position: 'fixed', inset: 0, zIndex }}
      />
      {/*
        Toujours montée : sur l'accueil (ou tant que l'élément n'est pas mesuré), c'est un point au
        centre, invisible dans le voile. Elle s'ouvre et glisse vers l'élément de l'étape suivante,
        comme d'une étape à l'autre, au lieu d'apparaître d'un coup.
      */}
      <Box
        data-testid="product-tour-spotlight"
        data-focused={focused}
        sx={{
          position: 'fixed',
          zIndex,
          pointerEvents: 'none',
          top: hole.top - pad,
          left: hole.left - pad,
          width: hole.width + pad * 2,
          height: hole.height + pad * 2,
          borderRadius: 1.5,
          boxShadow: `0 0 0 9999px ${scrim}`,
          outline: '2px solid',
          outlineColor: focused ? TOUR_OUTLINE : 'transparent',
          transition: theme.transitions.create(
            ['top', 'left', 'width', 'height', 'outline-color'],
            {
              duration: theme.transitions.duration.standard,
              easing: theme.transitions.easing.easeInOut,
            }
          ),
          /* Pulsation autour de l'élément, seulement quand il y en a un. */
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: -6,
            borderRadius: 2,
            border: `2px solid ${alpha(TOUR_OUTLINE, 0.8)}`,
            animation: `${pulse} 1.6s ease-out infinite`,
            display: focused ? 'block' : 'none',
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&::after': { animation: 'none', opacity: 0 },
          },
        }}
      />
      {resolving ? null : (
        <ProductTourCard
          /* Une carte par étape : floating-ui repart d'une position calculée, dans les deux sens. */
          key={step.id}
          intro={centered}
          title={step.title}
          body={step.body}
          stepIndex={Math.max(position, 0)}
          total={visibleSteps.length}
          onSelectStep={goToPosition}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          onPrevious={() => goTo(-1)}
          onNext={() => (hasNext ? goTo(1) : onClose('COMPLETED'))}
          onDismiss={() => onClose('DISMISSED')}
          titleId={titleId}
          primaryButtonRef={primaryButtonRef}
          zIndex={zIndex}
          floating={
            centered || oversized
              ? undefined
              : {
                  setRef: refs.setFloating,
                  style: {
                    ...floatingStyles,
                    visibility: isPositioned ? 'visible' : 'hidden',
                  },
                  context,
                  arrowRef,
                }
          }
        />
      )}
    </Portal>
  )
}
